import { FileCheck, Mail, Phone, PhoneCall, X } from 'lucide-react';
import React from 'react';
import { cn } from '../lib/utils';

/**
 * CandidateActionsModal Component
 * Professional modal with grid of action buttons
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.candidate - Current candidate
 * @param {Function} props.onGetEmail - Get email handler
 * @param {Function} props.onGetPhone - Get phone handler
 * @param {Function} props.onEvaluate - Evaluate handler
 * @param {Function} props.onCallAgent - Call agent handler
 */
export function CandidateActionsModal({
  isOpen,
  onClose,
  candidate,
  onGetEmail,
  onGetPhone,
  onEvaluate,
  onCallAgent
}) {
  if (!isOpen) return null;

  const handleAction = (action) => {
    action();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Actions for <span id="modal-candidate-name">{candidate?.name || 'Candidate'}</span>
              </h3>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Actions Grid */}
          <div className="p-6 grid grid-cols-2 gap-3">
            {/* Get Email */}
            <ActionButton
              icon={Mail}
              label="Get Email"
              variant="primary"
              onClick={() => handleAction(() => onGetEmail && onGetEmail(candidate))}
            />

            {/* Get Phone */}
            <ActionButton
              icon={Phone}
              label="Get Phone"
              variant="success"
              onClick={() => handleAction(() => onGetPhone && onGetPhone(candidate))}
            />

            {/* Evaluate */}
            <ActionButton
              icon={FileCheck}
              label="Evaluate"
              variant="warning"
              onClick={() => handleAction(() => onEvaluate && onEvaluate(candidate))}
            />

            {/* Call Agent */}
            <ActionButton
              icon={PhoneCall}
              label="Call Agent"
              variant="secondary"
              onClick={() => handleAction(() => onCallAgent && onCallAgent(candidate))}
            />
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
            <button
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * ActionButton Component
 * Individual action button in the modal
 */
function ActionButton({ icon: Icon, label, variant, onClick }) {
  const variantStyles = {
    primary: 'border-gray-200 hover:border-blue-500 hover:bg-blue-50 text-blue-600',
    success: 'border-gray-200 hover:border-green-500 hover:bg-green-50 text-green-600',
    warning: 'border-gray-200 hover:border-orange-500 hover:bg-orange-50 text-orange-600',
    secondary: 'border-gray-200 hover:border-purple-500 hover:bg-purple-50 text-purple-600',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'p-4 border rounded-lg transition group',
        variantStyles[variant]
      )}
    >
      <Icon className="w-8 h-8 mx-auto mb-2" />
      <span className="text-sm font-medium text-gray-900">{label}</span>
    </button>
  );
}



